import { Button, EmptyState, Icons } from '@dbrain/ui';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * v0.15a.3: an error while a page is drawn never leaves it white. The app falls back to "Bir sorun oldu" with
 * Yenile; a panel next to a conversation falls back on its own, so the conversation stays usable. A boundary
 * draws its children again when `resetKey` changes (another page) or its fallback's button asks it to.
 */
interface Props {
  children: ReactNode;
  fallback: (retry: () => void) => ReactNode;
  resetKey?: unknown;
  /** where it happened, for the console */
  name: string;
}

interface State {
  error: Error | null;
  resetKey: unknown;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null, resetKey: this.props.resetKey };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    return props.resetKey !== state.resetKey ? { error: null, resetKey: props.resetKey } : null;
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error(`[${this.props.name}] drawing failed`, error, info.componentStack);
  }

  retry = () => this.setState({ error: null });

  override render(): ReactNode {
    return this.state.error ? this.props.fallback(this.retry) : this.props.children;
  }
}

/** The whole app: the page could not be drawn. Yenile loads it again; the home page is a way out of a page that keeps failing. */
export function AppError() {
  const { t } = useTranslation();
  return (
    <div className="page page--center app-error" role="alert" data-testid="app-error">
      <EmptyState
        icon={<Icons.warning />}
        title={t('crash.title')}
        action={
          <>
            <Button onClick={() => window.location.reload()} data-testid="app-error-reload">
              {t('crash.reload')}
            </Button>
            <a className="ui-button ui-button--secondary" href="/">
              {t('crash.home')}
            </a>
          </>
        }
      >
        {t('crash.body')}
      </EmptyState>
    </div>
  );
}

/** A side panel: it could not be drawn, the conversation next to it goes on. Yenile draws the panel again. */
export function PanelError({ retry }: { retry: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="panel-error" role="alert" data-testid="panel-error">
      <EmptyState
        icon={<Icons.warning />}
        title={t('crash.title')}
        action={
          <Button variant="secondary" onClick={retry} data-testid="panel-error-retry">
            {t('crash.reload')}
          </Button>
        }
      >
        {t('crash.panelBody')}
      </EmptyState>
    </div>
  );
}
